<template>
    <div class="bg-white shadow overflow-hidden sm:rounded-lg mt-4">
        <div class="px-4 py-5 border-b border-gray-200 sm:px-6">
            <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  Activities
                </h3>
            </div>

          <div class="bg-white shadow overflow-hidden sm:rounded-md">
            <ul>
              <li v-for="activity in activities">
                <a href="#" @click="toggle(activity.id)" class="block hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out">
                  <div class="flex items-center px-4 py-4 sm:px-6">
                    <div class="min-w-0 flex-1 flex items-center">
                      <div class="flex-shrink-0">
                          <!-- user image/icon here -->
                      </div>
                      <div class="min-w-0 flex-1 px-4 md:grid md:grid-cols-2 md:gap-4">
                        <div>
                          <div class="text-sm leading-5 font-medium text-indigo-600 truncate">
                            <span v-if="activity.causer">{{ `${activity.causer.first_name} ${activity.causer.last_name}` }}</span>
                            <span v-else>system</span>
                            <span class="text-gray-500 font-normal">{{ activity.description }}</span>
                          </div>
                          <div class="mt-2 flex items-center text-sm leading-5 text-gray-500">
                            <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 36 36">
                              <g>
                                <g>
                                  <path d="M30.224,3.948h-1.098V2.75c0-1.517-1.197-2.75-2.67-2.75c-1.474,0-2.67,1.233-2.67,2.75v1.197h-2.74V2.75
                                    c0-1.517-1.197-2.75-2.67-2.75c-1.473,0-2.67,1.233-2.67,2.75v1.197h-2.74V2.75c0-1.517-1.197-2.75-2.67-2.75
                                    c-1.473,0-2.67,1.233-2.67,2.75v1.197H6.224c-2.343,0-4.25,1.907-4.25,4.25v24c0,2.343,1.907,4.25,4.25,4.25h24
                                    c2.344,0,4.25-1.907,4.25-4.25v-24C34.474,5.855,32.567,3.948,30.224,3.948z M25.286,2.75c0-0.689,0.525-1.25,1.17-1.25
                                    c0.646,0,1.17,0.561,1.17,1.25v4.896c0,0.689-0.524,1.25-1.17,1.25c-0.645,0-1.17-0.561-1.17-1.25V2.75z M17.206,2.75
                                    c0-0.689,0.525-1.25,1.17-1.25s1.17,0.561,1.17,1.25v4.896c0,0.689-0.525,1.25-1.17,1.25s-1.17-0.561-1.17-1.25V2.75z M9.125,2.75
                                    c0-0.689,0.525-1.25,1.17-1.25s1.17,0.561,1.17,1.25v4.896c0,0.689-0.525,1.25-1.17,1.25s-1.17-0.561-1.17-1.25V2.75z
                                     M31.974,32.198c0,0.965-0.785,1.75-1.75,1.75h-24c-0.965,0-1.75-0.785-1.75-1.75v-22h27.5V32.198z"/>
                                  <rect x="6.724" y="14.626" width="4.595" height="4.089"/>
                                  <rect x="12.857" y="14.626" width="4.596" height="4.089"/>
                                  <rect x="18.995" y="14.626" width="4.595" height="4.089"/>
                                  <rect x="25.128" y="14.626" width="4.596" height="4.089"/>
                                  <rect x="6.724" y="20.084" width="4.595" height="4.086"/>
                                  <rect x="12.857" y="20.084" width="4.596" height="4.086"/>
                                  <rect x="18.995" y="20.084" width="4.595" height="4.086"/>
                                  <rect x="25.128" y="20.084" width="4.596" height="4.086"/>
                                  <rect x="6.724" y="25.54" width="4.595" height="4.086"/>
                                  <rect x="12.857" y="25.54" width="4.596" height="4.086"/>
                                  <rect x="18.995" y="25.54" width="4.595" height="4.086"/>
                                  <rect x="25.128" y="25.54" width="4.596" height="4.086"/>
                                </g>
                              </g>
                            </svg>
                            <span class="truncate"><time :datetime="activity.created_at">{{ activity.created_at | localDateTime }}</time></span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <svg class="h-5 w-5 text-gray-400" :class="{'transform rotate-90 duration-100': active === activity.id}" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                      </svg>
                    </div>
                  </div>
                </a>
                <transition enter-active-class="transition ease-out duration-100"
                            enter-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95"
                >
                <div v-if="active === activity.id" class="p-4 text-sm text-gray-700">
                  <pre v-if="isSuperAdmin" style="white-space: pre-wrap; word-wrap: break-word;">{{ activity.properties }}</pre>
                  <div v-else>
                    Activity details can be viewed by super admin only.
                  </div>
                </div>
                </transition>
              </li>
            </ul>
          </div>
        </div>
    </div>
</template>
<script>
  export default {
    name: 'OrderHistory',
    props: {
      activities: Array,
      isSuperAdmin: Boolean
    },

    data() {
      return {
        active: null,
      }
    },

    methods: {
      toggle(activity_id) {
        if (this.active !== null && this.active === activity_id) {
          this.active = null
        } else {
          this.active = activity_id
        }
      }
    }
  }
</script>
